/**
 * Cart Store
 *
 * Zustand store for cart state management.
 * Integrates with the Cart API for server-side persistence.
 * Supports both guest (session-based) and authenticated users.
 */

import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { calculateCartPrice } from '@/api/pricing';
import { validateCart as validateCartApi, CartValidationResult } from '@/api/cart';
import { CartPricingRequest, CartPricingItemRequest } from '@/types';
import { getSessionId } from '@/hooks/useSession';
import { formatDate } from '@/lib/utils';
import { trackCartEvent } from '@/lib/telemetry/cartEvents';
import {
    CartItem,
    CartState,
    CartValidationError,
    CustomerCartResponse,
    CustomerCartItemResponse,
    PricingOptionType,
    SubscriptionDuration,
    ReservationHydrationPayload,
    ShelvedCart,
} from '@/types/cart';

export type { ReservationHydrationPayload, ShelvedCart } from '@/types/cart';
import {
    getPricingOptionLabel,
    getRentalTierFromDays,
    isNoCommitmentSubscription,
    normalizeRentalPricingTier,
    DEFAULT_PRICING_OPTION,
} from '@/utils/pricing';
import { isSubscriptionCartItem } from '@/utils/cartItemType';

const CART_DEBUG = import.meta.env.DEV && import.meta.env.MODE !== 'test';
const CART_WARNINGS_ENABLED = import.meta.env.MODE !== 'test';

// Re-export CartItem type for backwards compatibility
export type { CartItem } from '@/types/cart';

/**
 * Calculate rental days from start and end dates
 */
function calculateRentalDays(startDate: string, endDate: string): number {
    const start = new Date(startDate);
    const end = new Date(endDate);
    return Math.max(1, Math.round((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24)));
}

/**
 * Generate human-readable date display text for a cart item
 */
function generateDateDisplayText(item: {
    pricingOptionType?: PricingOptionType;
    /** @deprecated use pricingOptionType */
    isSubscription?: boolean;
    startDate: string;
    endDate?: string;
    subscriptionMonths?: number | SubscriptionDuration;
    rentalDays?: number;
}): string {
    const isSubscription = isSubscriptionCartItem(item);

    if (isSubscription) {
        const months = item.subscriptionMonths || 0;
        if (months === 0 || months === SubscriptionDuration.MonthToMonth) {
            return `Flexible monthly from ${formatDate(item.startDate)}`;
        }
        const monthText = months === 1 ? 'month' : 'months';
        return `${months} ${monthText} from ${formatDate(item.startDate)}`;
    }

    if (item.startDate && item.endDate) {
        const days = item.rentalDays || calculateRentalDays(item.startDate, item.endDate);
        const dayText = days === 1 ? 'day' : 'days';
        return `${formatDate(item.startDate)} - ${formatDate(item.endDate)} (${days} ${dayText})`;
    }

    return 'Dates not set';
}

/**
 * Compute derived fields for a cart item:
 * - rentalDays: Calculated from start/end dates for rentals
 * - dateDisplayText: Human-readable date/duration text
 * - pricingOptionLabel: Display label for the pricing option
 * - selectedRentalTier: Auto-calculated if not provided for rentals
 * - isNoCommitment: True for month-to-month subscriptions
 */
function computeDerivedFields(item: Partial<CartItem>): Pick<CartItem, 'rentalDays' | 'dateDisplayText' | 'pricingOptionLabel' | 'selectedRentalTier' | 'isNoCommitment'> {
    const isSubscription = isSubscriptionCartItem(item);
    const pricingOptionType = isSubscription
        ? PricingOptionType.MonthlySubscription
        : item.pricingOptionType ?? DEFAULT_PRICING_OPTION;

    // Calculate rental days for rentals
    const rentalDays = !isSubscription && item.startDate && item.endDate
        ? calculateRentalDays(item.startDate, item.endDate)
        : undefined;

    // Auto-calculate rental tier if not provided. Defensively normalize legacy
    // string-tier values that may live in persisted carts from before the
    // numeric/string mismatch was caught.
    let selectedRentalTier = normalizeRentalPricingTier(item.selectedRentalTier as unknown) ?? undefined;
    if (!isSubscription && rentalDays && selectedRentalTier === undefined) {
        selectedRentalTier = getRentalTierFromDays(rentalDays);
    }

    // Generate pricing option label
    const pricingOptionLabel = getPricingOptionLabel(
        pricingOptionType,
        selectedRentalTier,
        item.subscriptionMonths
    );

    // Calculate isNoCommitment for subscriptions
    const isNoCommitment = isSubscription
        ? isNoCommitmentSubscription(item.subscriptionMonths ?? 0)
        : undefined;

    // Generate date display text
    const dateDisplayText = item.startDate
        ? generateDateDisplayText({
            pricingOptionType,
            isSubscription,
            startDate: item.startDate,
            endDate: item.endDate,
            subscriptionMonths: item.subscriptionMonths,
            rentalDays,
        })
        : undefined;

    return { rentalDays, dateDisplayText, pricingOptionLabel, selectedRentalTier, isNoCommitment };
}

interface CartStore extends CartState {
    // Core cart actions
    addItem: (item: Omit<CartItem, 'id'>) => void;
    removeItem: (itemId: string) => void;
    updateItem: (itemId: string, updates: Partial<CartItem>) => void;
    updateItemQuantity: (itemId: string, quantity: number) => void;
    clearCart: () => void;

    // Context actions
    setLocation: (locationId: string) => void;

    // Pricing actions
    recalculatePrices: () => Promise<void>;

    // Validation actions
    validateCart: (startDate: string, endDate: string, locationId: string) => Promise<CartValidationResult | null>;
    setValidationErrors: (errors: CartValidationError[]) => void;
    clearValidationErrors: () => void;

    // API integration actions
    setCartFromApi: (cart: CustomerCartResponse) => void;
    setLoading: (isLoading: boolean) => void;
    setError: (error: string | null) => void;

    // UI actions
    openDrawer: () => void;
    closeDrawer: () => void;
    toggleDrawer: () => void;
    setLastAddedItem: (item: { name: string; image?: string } | null) => void;
    clearLastAddedItem: () => void;

    // Computed getters
    itemCount: () => number;
    isEmpty: () => boolean;
    hasValidationErrors: () => boolean;

    // Cart-shape selectors (for promo code validation)
    getRentalSubtotal: () => number;
    getMonthlySubtotal: () => number;
    hasShortTermItems: () => boolean;
    hasSubscriptionItems: () => boolean;

    // Admin reservation actions
    hydrateFromReservation: (payload: ReservationHydrationPayload) => void;
    clearReservation: () => void;
}

/**
 * Maps API cart item to store cart item
 */
const mapApiItemToCartItem = (item: CustomerCartItemResponse): CartItem => {
    // Determine pricing option from API response or infer from subscription-specific fields.
    const inferredSubscription = isSubscriptionCartItem(item);
    const pricingOptionType = inferredSubscription
        ? PricingOptionType.MonthlySubscription
        : item.pricingOptionType ?? PricingOptionType.Rental;
    const isSubscription = inferredSubscription;

    if (
        item.pricingOptionType !== undefined &&
        inferredSubscription &&
        item.pricingOptionType !== PricingOptionType.MonthlySubscription
    ) {
        if (CART_WARNINGS_ENABLED) {
            console.warn('[Cart] API cart item has conflicting subscription signals; inferring MonthlySubscription.', {
                itemId: item.id,
                pricingOptionType: item.pricingOptionType,
                isSubscription: item.isSubscription,
                subscriptionMonths: item.subscriptionMonths,
            });
        }
    }

    if (item.pricingOptionType === undefined && inferredSubscription) {
        if (CART_WARNINGS_ENABLED) {
            console.warn('[Cart] API cart item is missing pricingOptionType; inferring MonthlySubscription.', {
                itemId: item.id,
                isSubscription: item.isSubscription,
                subscriptionMonths: item.subscriptionMonths,
            });
        }
    }

    // Calculate derived fields
    const rentalDays = !isSubscription && item.startDate && item.endDate
        ? calculateRentalDays(item.startDate, item.endDate)
        : undefined;

    // Get or calculate rental tier. The API contract auto-generates this enum
    // as strings (`"Days8To14"`) while the in-app enum is numeric — without
    // this normalization, downstream switch statements miss every case.
    const normalizedFromApi = normalizeRentalPricingTier(item.selectedRentalTier as unknown);
    const selectedRentalTier = normalizedFromApi
        ?? (rentalDays ? getRentalTierFromDays(rentalDays) : undefined);

    // Get pricing label from API or calculate it
    const pricingOptionLabel = item.pricingOptionLabel ??
        getPricingOptionLabel(pricingOptionType, selectedRentalTier, item.subscriptionMonths ?? undefined);

    // Determine isNoCommitment for subscriptions
    const isNoCommitmentValue = isSubscription
        ? isNoCommitmentSubscription(item.subscriptionMonths ?? 0)
        : undefined;

    return {
        id: item.id || crypto.randomUUID(),
        equipmentId: item.equipmentId || '',
        equipmentTypeId: item.equipmentId || '', // Using equipmentId as equipmentTypeId
        productName: item.equipmentName || 'Unknown Product',
        productImage: item.imageUrl || undefined,
        locationId: item.locationId || '',
        locationName: item.locationName || undefined,
        quantity: item.quantity || 1,
        unitPrice: item.unitPrice || 0,
        startDate: item.startDate || '',
        endDate: item.endDate || '',
        rentalDays,
        totalPrice: item.totalPrice,
        depositAmount: item.depositAmount,
        pricing: item.pricingBreakdown,
        // Pricing option fields
        pricingOptionType,
        pricingOptionLabel,
        selectedRentalTier,
        subscriptionMonths: item.subscriptionMonths ?? undefined,
        subscriptionMonthlyRate: item.subscriptionMonthlyRate ?? undefined,
        isNoCommitment: isNoCommitmentValue,
        isNewItem: item.isNewItem ?? false,
        /** @deprecated - Use pricingOptionType === MonthlySubscription */
        isSubscription: isSubscription,
    };
};

let latestPricingRequest = 0;

export const useCartStore = create<CartStore>()(
    persist(
        (set, get) => ({
            // Initial state
            sessionId: null,
            items: [],
            locationId: undefined,
            subtotal: 0,
            totalDeposit: 0,
            totalDiscount: 0,
            recurringDiscountAmount: 0,
            grandTotal: 0,
            vatRate: 0,
            vatAmount: 0,
            calculatedAt: undefined,
            isLoading: false,
            isDrawerOpen: false,
            error: null,
            lastAddedItem: null,
            isValidating: false,
            validationErrors: [],

            // Admin reservation lock state
            isLocked: false,
            reservationId: null,
            resumeToken: null,
            shelvedCart: null,
            locationName: null,
            adminNote: null,

            // =================================================================
            // Core Cart Actions
            // =================================================================

            addItem: (item) => {
                // Validate required location
                if (!item.locationId || item.locationId === '') {
                    if (CART_WARNINGS_ENABLED) {
                        console.error('[Cart] Cannot add item without location');
                    }
                    set({ error: 'Location is required - please select a pickup location' });
                    return;
                }

                // Validate required dates
                if (!item.startDate) {
                    if (CART_WARNINGS_ENABLED) {
                        console.error('[Cart] Cannot add item without start date');
                    }
                    set({ error: 'Start date is required' });
                    return;
                }

                // Determine pricing option type (support both new and deprecated fields)
                const pricingOptionType = item.pricingOptionType ??
                    (isSubscriptionCartItem(item) ? PricingOptionType.MonthlySubscription : DEFAULT_PRICING_OPTION);
                const isSubscription = isSubscriptionCartItem({
                    ...item,
                    pricingOptionType,
                });

                // Rentals require end date, subscriptions don't
                if (!isSubscription && !item.endDate) {
                    if (CART_WARNINGS_ENABLED) {
                        console.error('[Cart] Rental items require end date');
                    }
                    set({ error: 'End date is required for rentals' });
                    return;
                }

                // Validate subscription has duration (0 is valid - means month-to-month)
                if (isSubscription && item.subscriptionMonths === undefined) {
                    if (CART_WARNINGS_ENABLED) {
                        console.error('[Cart] Subscription items require duration');
                    }
                    set({ error: 'Subscription duration is required' });
                    return;
                }

                // Compute derived fields (includes pricingOptionLabel, selectedRentalTier, isNoCommitment)
                const derivedFields = computeDerivedFields({
                    ...item,
                    pricingOptionType,
                });

                const newItem: CartItem = {
                    ...item,
                    id: crypto.randomUUID(),
                    pricingOptionType,
                    /** @deprecated - Use pricingOptionType */
                    isSubscription,
                    ...derivedFields,
                };

                if (CART_DEBUG) {
                    console.log('[Cart] Adding item:', {
                        name: newItem.productName,
                        pricingOptionType: newItem.pricingOptionType,
                        pricingOptionLabel: newItem.pricingOptionLabel,
                        isSubscription: newItem.isSubscription,
                        startDate: newItem.startDate,
                        endDate: newItem.endDate,
                        rentalDays: newItem.rentalDays,
                        selectedRentalTier: newItem.selectedRentalTier,
                        subscriptionMonths: newItem.subscriptionMonths,
                        isNoCommitment: newItem.isNoCommitment,
                        dateDisplayText: newItem.dateDisplayText,
                    });
                }

                set((state) => ({ items: [...state.items, newItem], error: null }));
                get().recalculatePrices();

                // Track event after state update
                trackCartEvent.itemAdded(
                    newItem.equipmentTypeId,
                    newItem.productName,
                    newItem.quantity,
                    newItem.unitPrice || 0,
                    isSubscriptionCartItem(newItem)
                );
            },

            removeItem: (itemId) => {
                const item = get().items.find(i => i.id === itemId);

                set((state) => ({
                    items: state.items.filter((i) => i.id !== itemId),
                }));
                get().recalculatePrices();

                // Track event if item existed
                if (item) {
                    trackCartEvent.itemRemoved(
                        item.equipmentTypeId,
                        item.productName,
                        item.quantity
                    );
                }
            },

            updateItem: (itemId, updates) => {
                const item = get().items.find(i => i.id === itemId);

                set((state) => ({
                    items: state.items.map((item) => {
                        if (item.id !== itemId) return item;

                        const updatedItem = { ...item, ...updates };

                        // Recompute derived fields if dates, pricing option, or subscription status changed
                        if (
                            updates.startDate ||
                            updates.endDate ||
                            updates.pricingOptionType !== undefined ||
                            updates.selectedRentalTier !== undefined ||
                            updates.isSubscription !== undefined ||
                            updates.subscriptionMonths !== undefined
                        ) {
                            const derivedFields = computeDerivedFields(updatedItem);
                            return { ...updatedItem, ...derivedFields };
                        }

                        return updatedItem;
                    }),
                }));
                get().recalculatePrices();

                // Track date changes if applicable
                if (item && updates.startDate && updates.endDate) {
                    trackCartEvent.datesChanged(
                        item.equipmentTypeId,
                        item.startDate || '',
                        item.endDate || '',
                        updates.startDate,
                        updates.endDate
                    );
                }
            },

            updateItemQuantity: (itemId, quantity) => {
                const item = get().items.find(i => i.id === itemId);
                const oldQty = item?.quantity || 0;

                if (quantity <= 0) {
                    get().removeItem(itemId);
                    return;
                }
                set((state) => ({
                    items: state.items.map((i) =>
                        i.id === itemId ? { ...i, quantity } : i
                    ),
                }));

                // Track quantity change
                if (item && oldQty !== quantity) {
                    trackCartEvent.quantityChanged(item.equipmentTypeId, oldQty, quantity);
                }
            },

            clearCart: () =>
                set({
                    items: [],
                    locationId: undefined,
                    subtotal: 0,
                    totalDeposit: 0,
                    totalDiscount: 0,
                    recurringDiscountAmount: 0,
                    grandTotal: 0,
                    vatRate: 0,
                    vatAmount: 0,
                    calculatedAt: undefined,
                    validationErrors: [],
                    error: null,
                    isLocked: false,
                    reservationId: null,
                    resumeToken: null,
                    shelvedCart: null,
                    locationName: null,
                    adminNote: null,
                }),

            // =================================================================
            // Context Actions
            // =================================================================

            setLocation: (locationId) => {
                set({ locationId });
                get().recalculatePrices();
            },

            // =================================================================
            // Pricing Actions
            // =================================================================

            /**
             * Recalculate prices by calling the unified pricing API.
             * Sends ALL cart items (both rentals and subscriptions) in a single API call.
             * Each item includes its own dates/subscription parameters.
             */
            recalculatePrices: async () => {
                const requestId = ++latestPricingRequest;
                const { items, locationId, isLocked, reservationId, resumeToken, recurringDiscountAmount: lockedRecurringDiscount } = get();

                if (CART_DEBUG) {
                    console.log('[Cart] recalculatePrices called with', items.length, 'items');
                    console.log('[Cart] Items detail:', items.map(i => ({
                        name: i.productName,
                        pricingOptionType: i.pricingOptionType,
                        pricingOptionLabel: i.pricingOptionLabel,
                        isSubscription: i.isSubscription,
                        startDate: i.startDate,
                        endDate: i.endDate,
                        subscriptionMonths: i.subscriptionMonths,
                    })));
                }

                // Handle empty cart
                if (items.length === 0) {
                    if (CART_DEBUG) console.log('[Cart] Empty cart, resetting prices');
                    set({
                        subtotal: 0,
                        totalDeposit: 0,
                        grandTotal: 0,
                        totalDiscount: 0,
                        recurringDiscountAmount: 0,
                        vatRate: 0,
                        vatAmount: 0,
                        calculatedAt: undefined,
                        error: null,
                    });
                    return;
                }

                // Validate that all items have required fields
                const itemsWithMissingData = items.filter(item => {
                    const isSubscription = isSubscriptionCartItem(item);
                    if (isSubscription) {
                        // Subscriptions need a start date and subscription months (0 is valid = month-to-month)
                        return !item.startDate || item.subscriptionMonths === undefined;
                    } else {
                        // Rentals need start and end dates
                        return !item.startDate || !item.endDate;
                    }
                });

                if (itemsWithMissingData.length > 0) {
                    if (CART_WARNINGS_ENABLED) {
                        console.warn('[Cart] Some items missing required data:', itemsWithMissingData);
                    }
                    set({ error: 'Some items are missing dates or duration' });
                    return;
                }

                // Build unified request with per-item dates
                const request: CartPricingRequest = {
                    items: items.map((item): CartPricingItemRequest => {
                        const isSubscription = isSubscriptionCartItem(item);
                        return {
                            equipmentTypeId: item.equipmentTypeId,
                            quantity: item.quantity,
                            isSubscription: isSubscription,
                            startDate: item.startDate,
                            endDate: isSubscription ? undefined : item.endDate,
                            subscriptionMonths: isSubscription ? item.subscriptionMonths : undefined,
                            isNewItem: item.isNewItem ?? false,
                        };
                    }),
                    locationId: items[0]?.locationId || locationId || undefined,
                    // For a resumed admin reservation, send the reservation context so the
                    // backend returns the LOCKED snapshot (agreed prices + promo discount)
                    // instead of re-pricing against the live catalogue.
                    ...(isLocked && reservationId && resumeToken ? { reservationId, resumeToken } : {}),
                };

                if (CART_DEBUG) console.log('[Cart] Calling unified pricing API with:', request);

                try {
                    const response = await calculateCartPrice(request);
                    // A remove, clear, API refresh or reservation switch may have
                    // replaced these items while pricing was in flight.
                    if (requestId !== latestPricingRequest || get().items !== items) return;
                    if (CART_DEBUG) console.log('[Cart] API response:', response);

                    // Update items with API pricing response
                    const updatedItems = items.map((item, index) => {
                        const itemPricing = response.items?.[index];
                        const isPriceAvailable = itemPricing?.isPriceAvailable !== false;

                        return {
                            ...item,
                            pricing: itemPricing,
                            // PRC-G2 (#517): persist the availability flag so the cart/checkout
                            // can block an unpriced line instead of falling back to a stale local
                            // price. Only overwrite when this index has an API row; otherwise keep
                            // whatever the line already carried.
                            isPriceAvailable: itemPricing ? isPriceAvailable : item.isPriceAvailable,
                            // Sync unitPrice from API response
                            unitPrice: isPriceAvailable && itemPricing?.unitPrice != null
                                ? itemPricing.unitPrice
                                : item.unitPrice,
                            // Only overwrite price if API confirms pricing is available
                            totalPrice: isPriceAvailable && itemPricing?.lineTotal != null
                                ? itemPricing.lineTotal
                                : item.totalPrice,
                            depositAmount: isPriceAvailable && itemPricing?.itemDeposit != null
                                ? itemPricing.itemDeposit
                                : item.depositAmount,
                            productName: itemPricing?.equipmentTypeName || item.productName,
                            // Sync subscription monthly rate from API response if available
                            subscriptionMonthlyRate: itemPricing?.monthlyRate ?? item.subscriptionMonthlyRate,
                        };
                    });

                    set({
                        items: updatedItems,
                        subtotal: response.subtotal || 0,
                        totalDeposit: response.totalDeposit || 0,
                        // Non-zero only for a resumed reservation's locked snapshot; the
                        // backend returns the promo discount + an already-discounted total.
                        totalDiscount: response.totalDiscount || 0,
                        // For a locked reservation the recurring promo discount comes from the
                        // resume snapshot (set by hydrateFromReservation); the cart-pricing
                        // response has no recurring breakdown, so preserve it instead of zeroing
                        // it — otherwise the recurring-monthly line reverts to the gross rate.
                        recurringDiscountAmount: isLocked ? lockedRecurringDiscount : 0,
                        grandTotal: response.amountDueNow
                            ?? (response.subtotal || 0) + (response.totalDeposit || 0) + (response.deliveryFee || 0),
                        vatRate: response.vatRate ?? 0,
                        vatAmount: response.vatAmount ?? 0,
                        calculatedAt: response.calculatedAt || new Date().toISOString(),
                        error: null,
                    });
                } catch (error) {
                    if (requestId !== latestPricingRequest || get().items !== items) return;
                    if (CART_WARNINGS_ENABLED) {
                        console.error('[Cart] Failed to calculate prices:', error);
                    }
                    const errorMessage = error instanceof Error ? error.message : 'Failed to calculate prices';
                    set({ error: `Pricing error: ${errorMessage}` });
                }
            },

            // =================================================================
            // API Integration Actions
            // =================================================================

            setCartFromApi: (cart: CustomerCartResponse) => {
                // When an admin reservation is loaded the cart is LOCKED to the
                // reservation's snapshot — the customer's server cart must never
                // override it. Layout mounts useCart() globally, so this fires
                // during the resume → checkout flow; without this guard it wipes
                // the just-hydrated reservation with the (empty) server cart and
                // the checkout empty-cart guard bounces the user to /cart.
                if (get().isLocked) {
                    return;
                }

                const items = (cart.items || []).map(mapApiItemToCartItem);

                set({
                    sessionId: cart.sessionId || getSessionId(),
                    items,
                    subtotal: cart.subtotal || 0,
                    totalDeposit: cart.depositAmount || 0,
                    totalDiscount: cart.discountAmount || 0,
                    recurringDiscountAmount: 0,
                    grandTotal: cart.totalAmount || 0,
                    calculatedAt: cart.updatedAt,
                });
            },

            setLoading: (isLoading) => set({ isLoading }),

            setError: (error) => set({ error }),

            // =================================================================
            // UI Actions
            // =================================================================

            openDrawer: () => set({ isDrawerOpen: true }),

            closeDrawer: () => set({ isDrawerOpen: false }),

            toggleDrawer: () =>
                set((state) => ({ isDrawerOpen: !state.isDrawerOpen })),

            setLastAddedItem: (item) => set({ lastAddedItem: item }),

            clearLastAddedItem: () => set({ lastAddedItem: null }),

            // =================================================================
            // Validation Actions
            // =================================================================

            /**
             * Validate cart items for availability at new dates/location
             * Returns validation result with any errors
             */
            validateCart: async (startDate: string, endDate: string, locationId: string) => {
                const { items } = get();
                if (items.length === 0) {
                    set({ validationErrors: [], isValidating: false });
                    return null;
                }

                set({ isValidating: true, validationErrors: [] });

                try {
                    const result = await validateCartApi({
                        startDate,
                        endDate,
                        locationId,
                    });

                    // Map API validation errors to store format
                    const errors: CartValidationError[] = [];

                    // Process equipment item validation errors
                    for (const item of result.items || []) {
                        if (!item.canFulfill || item.priceChanged) {
                            errors.push({
                                itemId: item.itemId,
                                equipmentName: item.equipmentName,
                                error: item.error || (item.priceChanged
                                    ? `Price changed from €${item.originalUnitPrice?.toFixed(2)} to €${item.newUnitPrice?.toFixed(2)}`
                                    : 'Item no longer available'),
                                availableQuantity: item.availableQuantity,
                                requestedQuantity: item.requestedQuantity,
                                priceChanged: item.priceChanged,
                                newPrice: item.newUnitPrice,
                                isAccessory: false,
                            });
                        }
                    }

                    // Process accessory item validation errors
                    for (const accessory of result.accessoryItems || []) {
                        if (!accessory.canFulfill || accessory.priceChanged) {
                            errors.push({
                                itemId: accessory.itemId,
                                equipmentName: accessory.accessoryName,
                                error: accessory.error || (accessory.priceChanged
                                    ? `Price changed from €${accessory.originalUnitPrice?.toFixed(2)} to €${accessory.newUnitPrice?.toFixed(2)}`
                                    : 'Accessory no longer available'),
                                availableQuantity: accessory.availableQuantity,
                                requestedQuantity: accessory.requestedQuantity,
                                priceChanged: accessory.priceChanged,
                                newPrice: accessory.newUnitPrice,
                                isAccessory: true,
                            });
                        }
                    }

                    set({ validationErrors: errors, isValidating: false });
                    return result;
                } catch (error) {
                    if (CART_WARNINGS_ENABLED) {
                        console.error('Cart validation failed', error);
                    }
                    set({
                        isValidating: false,
                        error: 'Failed to validate cart. Please try again.',
                    });
                    return null;
                }
            },

            setValidationErrors: (errors) =>
                set({ validationErrors: errors, isValidating: false }),

            clearValidationErrors: () =>
                set({ validationErrors: [], isValidating: false }),

            // =================================================================
            // Admin Reservation Actions
            // =================================================================

            /**
             * Hydrate the cart from an admin-initiated reservation.
             *
             * If the current cart already contains items, those items (and the
             * current locationId) are snapshotted into `shelvedCart` so they can
             * be restored when the reservation is cleared.
             *
             * The cart items are replaced with the reservation's items, totals
             * are locked to the admin-supplied values, and `isLocked` is set to
             * true so the UI can prevent editing.
             */
            hydrateFromReservation: (payload: ReservationHydrationPayload) => {
                const { items: currentItems, locationId: currentLocationId } = get();

                // Snapshot existing cart only if it has items
                const shelvedCart: ShelvedCart | null = currentItems.length > 0
                    ? { items: currentItems, locationId: currentLocationId ?? null }
                    : null;

                // Map payload items to CartItem shape
                const newItems: CartItem[] = payload.items.map((pi) => {
                    const pricingOptionType: PricingOptionType = pi.pricingOptionType === 1
                        ? PricingOptionType.MonthlySubscription
                        : PricingOptionType.Rental;
                    const isSubscription = pricingOptionType === PricingOptionType.MonthlySubscription;

                    const derivedFields = computeDerivedFields({
                        pricingOptionType,
                        startDate: payload.pickupDate,
                        endDate: isSubscription ? undefined : payload.returnDate,
                        subscriptionMonths: pi.subscriptionMonths ?? undefined,
                    });

                    return {
                        id: pi.id ?? crypto.randomUUID(),
                        depositAmount: pi.depositAmount,
                        equipmentId: pi.equipmentTypeId,
                        equipmentTypeId: pi.equipmentTypeId,
                        productName: pi.equipmentTypeName ?? 'Unknown Product',
                        productImage: pi.imageUrl ?? undefined,
                        locationId: payload.locationId ?? '',
                        locationName: payload.locationName,
                        quantity: pi.quantity,
                        unitPrice: pi.unitPrice,
                        startDate: payload.pickupDate,
                        endDate: isSubscription ? undefined : payload.returnDate,
                        totalPrice: pi.totalPrice,
                        pricingOptionType,
                        isSubscription,
                        subscriptionMonths: pi.subscriptionMonths ?? undefined,
                        isNewItem: false,
                        ...derivedFields,
                    };
                });

                set({
                    items: newItems,
                    locationId: payload.locationId ?? undefined,
                    subtotal: payload.lockedSubtotal,
                    totalDeposit: payload.lockedDeposit,
                    // grandTotal already has the promo discount subtracted server-side.
                    grandTotal: payload.lockedTotal,
                    // Surface the promo discount so the locked cart renders the discount
                    // line + discounted total, exactly like a regular cart with a promo.
                    totalDiscount: payload.appliedDiscountAmount ?? 0,
                    recurringDiscountAmount: payload.appliedDiscountRecurringAmount ?? 0,
                    isLocked: true,
                    reservationId: payload.reservationId,
                    resumeToken: payload.resumeToken,
                    shelvedCart,
                    locationName: payload.locationName ?? null,
                    adminNote: payload.adminNote ?? null,
                    error: null,
                    validationErrors: [],
                });
            },

            /**
             * Clear an active admin reservation and restore the previously
             * shelved cart (if any). If no cart was shelved, resets to empty.
             */
            clearReservation: () => {
                const { shelvedCart } = get();

                if (shelvedCart) {
                    set({
                        items: shelvedCart.items,
                        locationId: shelvedCart.locationId ?? undefined,
                        isLocked: false,
                        reservationId: null,
                        resumeToken: null,
                        shelvedCart: null,
                        locationName: null,
                        adminNote: null,
                        error: null,
                        validationErrors: [],
                    });
                } else {
                    set({
                        items: [],
                        locationId: undefined,
                        subtotal: 0,
                        totalDeposit: 0,
                        totalDiscount: 0,
                        recurringDiscountAmount: 0,
                        grandTotal: 0,
                        vatRate: 0,
                        vatAmount: 0,
                        calculatedAt: undefined,
                        isLocked: false,
                        reservationId: null,
                        resumeToken: null,
                        shelvedCart: null,
                        locationName: null,
                        adminNote: null,
                        error: null,
                        validationErrors: [],
                    });
                }
            },

            // =================================================================
            // Computed Getters
            // =================================================================

            itemCount: () =>
                get().items.reduce((sum, item) => sum + item.quantity, 0),

            isEmpty: () => get().items.length === 0,

            hasValidationErrors: () => get().validationErrors.length > 0,

            getRentalSubtotal: () => {
                const items = get().items ?? [];
                return items
                    .filter((it) => !isSubscriptionCartItem(it))
                    .reduce((acc, it) => acc + (it.totalPrice ?? 0), 0);
            },

            getMonthlySubtotal: () => {
                const items = get().items ?? [];
                return items
                    .filter((it) => isSubscriptionCartItem(it))
                    .reduce((acc, it) => acc + (it.totalPrice ?? 0), 0);
            },

            hasShortTermItems: () => (get().items ?? []).some((it) => !isSubscriptionCartItem(it)),

            hasSubscriptionItems: () => (get().items ?? []).some((it) => isSubscriptionCartItem(it)),
        }),
        {
            name: 'stork-cart-storage',
            storage: createJSONStorage(() => localStorage),
            merge: (persistedState, currentState) => {
                const state = { ...currentState, ...(persistedState as Partial<CartStore>) };
                // Repair orphaned locks from older persisted carts. A real
                // reservation needs both credentials and items to resume.
                if (state.isLocked && (!state.reservationId || !state.resumeToken || state.items.length === 0)) {
                    state.isLocked = false;
                    state.reservationId = null;
                    state.resumeToken = null;
                    state.shelvedCart = null;
                    state.locationName = null;
                    state.adminNote = null;
                }
                return state;
            },
            version: 4,
            migrate: (persistedState: unknown, fromVersion: number) => {
                const state = (persistedState ?? {}) as Record<string, unknown>;
                if (fromVersion < 3) {
                    // v2 → v3: new reservation lock fields default to safe values
                    state.isLocked = state.isLocked ?? false;
                    state.reservationId = state.reservationId ?? null;
                    state.resumeToken = state.resumeToken ?? null;
                    state.shelvedCart = state.shelvedCart ?? null;
                }
                if (fromVersion < 4) {
                    // v3 → v4: new locationName + adminNote fields default to null
                    state.locationName = state.locationName ?? null;
                    state.adminNote = state.adminNote ?? null;
                }
                return state;
            },
            // Only persist essential data, not UI state
            partialize: (state) => ({
                items: state.items,
                locationId: state.locationId,
                subtotal: state.subtotal,
                totalDeposit: state.totalDeposit,
                totalDiscount: state.totalDiscount,
                recurringDiscountAmount: state.recurringDiscountAmount,
                grandTotal: state.grandTotal,
                vatRate: state.vatRate,
                vatAmount: state.vatAmount,
                // Admin reservation lock state (persisted so checkout survives reload)
                isLocked: state.isLocked,
                reservationId: state.reservationId,
                resumeToken: state.resumeToken,
                shelvedCart: state.shelvedCart,
                locationName: state.locationName,
                adminNote: state.adminNote,
            }),
        }
    )
);
